import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Empty, Popover, Radio, Row, Spin, Table, Typography } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { PmsAPI } from "../../../../controller/PmsAPI";
import ResponsiveECharts from "../../../components/ResponsiveECharts";
import { UP_COLOR, DOWN_COLOR, MUTED_COLOR } from "../../../components/metricColors";
import {
  CHANNELS, CHANNEL_LABEL, formatShare, money, kpiDelta, visitsOption, commissionOption, flowRows,
} from "./repeatGuestData";

const { Title, Text } = Typography;

// 頁面上的口徑說明。與 spec 第 3 節一致，改口徑要兩邊一起改。
// 不寫死任何數字 - 數字每天在變，寫進說明很快就過時，看表格本身就有。
// 「可爭取」「流失中」的解說各自寫在同一行，標籤與意思不拆開 (測試據此比對)。
const HELP_SECTIONS = [
  {
    title: "回頭客怎麼算",
    items: [
      "只看散客，團體不計。",
      "同一人以身分證認定，重複建檔的多筆主檔會合併；手機不拿來合併 (家人、訂房人常共用)。",
      "換房、續住併成同一次來訪。",
      "第 2 次以後的每一次來訪都算一次「回訪」；本頁數的是回訪次數，不是人數。",
      "每次回訪歸到入住日所在的月份；KPI 比較近 12 個完整月與再往前 12 個月，不含本月。",
    ],
  },
  {
    title: "通路與直訂流失",
    items: [
      "每次來訪的通路取第一張訂單的訂房來源：Booking、Agoda 等網路旅行社 → OTA；電話、官網、現場 → 直訂；其餘 (旅行社、合約廠商、住宿券等) → 其他。",
      "官網訂單在德安裡常被標成「網路旅行社」，本頁依訂房來源名稱「官網訂房」辨識，一律算直訂。",
      "「直訂流失」= 上次直訂、這次改走 OTA。指的是訂房通路的流失，不是客人不再回來。",
      "佣金依「設定 → 通路佣金」即時計算，官網的 2% 手續費照列。",
    ],
  },
  {
    title: "通路流向表怎麼看",
    items: [
      "列是上次的通路，欄是這次的通路；每格是這個組合的回訪次數、房晚、房費與佣金。",
      "可爭取 (OTA → OTA)：客人已經認得飯店，這次仍透過 OTA 訂房、照樣付佣金；讓他們下次改直訂，省下的就是這筆佣金。",
      "流失中 (直訂 → OTA)：原本直訂、不必付佣金的客人改走 OTA，通常代表 OTA 的價格或方便性比直訂更有吸引力。",
      "OTA → 直訂是好的方向，越大越好；目標是讓「可爭取」與「流失中」兩格變小。",
    ],
  },
  {
    title: "限制",
    items: [
      "只看有回訪的紀錄；只來過一次的客人不在這頁。",
      "約 45% 的住客沒有身分證號，回訪也串不起來，所以回訪數是下限。",
      "資料自 2019/02 起，越早的年份回訪越被低估。",
      "電話訂單裡有少數其實是旅行社代訂 (約 3%)，目前仍算直訂。",
    ],
  },
];

// 限高、框內捲動：整框比畫面高時 antd 會把它往上推、蓋住問號本身，看起來像關不掉
// (2026-10-08 桌面 900px 高實測說明框 869px，頂到畫面上緣並蓋住標題)。
const DEFINITION = (
  <div data-help-body style={{ lineHeight: 1.7, maxHeight: "min(60vh, 560px)", overflowY: "auto", paddingRight: 4 }}>
    {HELP_SECTIONS.map((section, i) => (
      <div key={section.title} style={{ marginTop: i ? 12 : 0 }}>
        <Text strong>{section.title}</Text>
        {section.items.map((item) => <div key={item}>{`・${item}`}</div>)}
      </div>
    ))}
  </div>
);

const TONE_COLOR = { good: UP_COLOR, bad: DOWN_COLOR, flat: MUTED_COLOR };

const KPIS = [
  { key: "repeat_visits", title: "回訪次數", format: (v) => v.toLocaleString(), lowerIsBetter: false },
  { key: "ota_share", title: "回訪走 OTA 的比例", format: formatShare, lowerIsBetter: true, kind: "share" },
  { key: "commission", title: "回訪付出的佣金", format: money, lowerIsBetter: true, kind: "money" },
  { key: "leak_visits", title: "直訂流失 (上次直訂、這次 OTA)", format: (v) => v.toLocaleString(), lowerIsBetter: true },
];

// 兩格要盯的流向：已經回來卻還在付佣金的，以及正在從直訂流回 OTA 的
const HIGHLIGHT = {
  "OTA>OTA": { label: "可爭取", background: "#fff7e6" },
  "DIRECT>OTA": { label: "流失中", background: "#fff1f0" },
};

const flowColumns = [
  { title: "上次 \\ 這次", dataIndex: "prev", width: 110, fixed: "left", render: (v) => CHANNEL_LABEL[v] },
  ...CHANNELS.map((cur) => ({
    title: CHANNEL_LABEL[cur],
    key: cur,
    width: 230,
    onCell: (row) => ({ style: { background: (HIGHLIGHT[`${row.prev}>${cur}`] || {}).background } }),
    render: (_, row) => {
      const cell = row[cur];
      const mark = HIGHLIGHT[`${row.prev}>${cur}`];
      return (
        <div style={{ whiteSpace: "nowrap" }}>
          <div>
            {`${cell.visits.toLocaleString()} 次・${cell.nights.toLocaleString()} 晚`}
            {mark && <Text strong style={{ marginLeft: 8 }}>{mark.label}</Text>}
          </div>
          <Text type="secondary">{`${money(cell.amount)}・佣金 ${money(cell.commission)}`}</Text>
        </div>
      );
    },
  })),
];
const FLOW_WIDTH = 110 + 230 * 3;

const platformColumns = [
  { title: "平台", dataIndex: "platform", width: 160 },
  { title: "回訪次數", dataIndex: "visits", width: 100, align: "right", render: (v) => v.toLocaleString() },
  { title: "房晚", dataIndex: "nights", width: 90, align: "right", render: (v) => v.toLocaleString() },
  { title: "房費", dataIndex: "amount", width: 150, align: "right", render: money },
  { title: "佣金", dataIndex: "commission", width: 140, align: "right", render: money },
];
const PLATFORM_WIDTH = 160 + 100 + 90 + 150 + 140;

/**
 * 報表 → 回頭客分析。給管理層看回頭客走 OTA 的比例、付出的佣金與直訂流失的趨勢，
 * 決定要不要投資直訂誘因。只有彙總數字，不列任何住客。
 */
export default function RepeatGuestPage() {
  const [unit, setUnit] = useState("month");
  const [report, setReport] = useState(null);
  // 初值 true：effect 跑之前的第一次渲染若是 false，會先閃一下「無資料」
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = (nextUnit) => {
    setLoading(true);
    setError(null);
    PmsAPI.repeatGuests.index(nextUnit)
      .then((res) => setReport(res.data))
      .catch((err) => {
        setReport(null);
        setError((err && err.response && err.response.data && err.response.data.message) || "讀取回頭客分析失敗，請稍後重試");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(unit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  const kpi = report && report.kpi;

  return (
    <div>
      <Title level={3} style={{ display: "flex", alignItems: "center", gap: 8 }}>
        回頭客分析
        {/* 內容長，用點擊開啟的 Popover 而不是 hover 提示：手機沒有 hover，長提示也會跟著滑鼠閃 */}
        <Popover
          title="口徑說明"
          content={DEFINITION}
          trigger="click"
          placement="bottomLeft"
          visible={helpOpen}
          onVisibleChange={setHelpOpen}
          overlayStyle={{ maxWidth: "min(480px, calc(100vw - 32px))" }}
        >
          <QuestionCircleOutlined
            style={{ fontSize: 16, color: MUTED_COLOR }}
            aria-label="口徑說明"
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              setHelpOpen(!helpOpen);
            }}
          />
        </Popover>
      </Title>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={<Button size="small" onClick={() => load(unit)}>重試</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        {!report && !loading && !error && <Empty />}

        {kpi && (
          <>
            <Text type="secondary">{`本期 ${kpi.current.from} ~ ${kpi.current.to}，與 ${kpi.previous.from} ~ ${kpi.previous.to} 比較 (不含當月)`}</Text>
            <Row gutter={[16, 16]} style={{ margin: "8px 0 16px" }}>
              {KPIS.map((k) => {
                const delta = kpiDelta(kpi.current[k.key], kpi.previous[k.key], k.lowerIsBetter, k.kind);
                return (
                  <Col key={k.key} xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Text type="secondary">{k.title}</Text>
                      <div style={{ fontSize: 26, fontWeight: 600, whiteSpace: "nowrap" }}>{k.format(kpi.current[k.key])}</div>
                      <Text style={{ color: TONE_COLOR[delta.tone], whiteSpace: "nowrap" }}>{`比前 12 個月 ${delta.text}`}</Text>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            <Card
              size="small"
              title="回訪次數 (依這次的訂房通路)"
              extra={(
                <Radio.Group value={unit} onChange={(e) => setUnit(e.target.value)} size="small" optionType="button">
                  <Radio.Button value="month">月</Radio.Button>
                  <Radio.Button value="year">年</Radio.Button>
                </Radio.Group>
              )}
              style={{ marginBottom: 16 }}
            >
              <ResponsiveECharts option={visitsOption(report.series)} />
            </Card>

            <Card size="small" title="回訪付出的佣金" style={{ marginBottom: 16 }}>
              <ResponsiveECharts option={commissionOption(report.series)} />
            </Card>

            <Card size="small" title="通路流向 (上次 → 這次，本期)" style={{ marginBottom: 16 }}>
              <Table
                size="small"
                rowKey="prev"
                columns={flowColumns}
                dataSource={flowRows(report.flows)}
                pagination={false}
                scroll={{ x: FLOW_WIDTH }}
              />
            </Card>

            <Card size="small" title="回頭客走哪些 OTA (本期)">
              <Table
                size="small"
                rowKey="platform"
                columns={platformColumns}
                dataSource={report.platforms}
                pagination={false}
                scroll={{ x: PLATFORM_WIDTH }}
                locale={{ emptyText: "本期沒有走 OTA 的回訪" }}
              />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
}
